import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, type EventDetail, type PlayerScorecard } from '../../api/public'
import { Empty, Failed } from '../../components/Async'
import { LiveLoading, LiveStatus } from '../../components/Live'
import { MatchHoles, ProvenanceMark, Replays } from '../../components/MatchHoles'
import { NetSoFar } from '../../components/NetSoFar'
import { Placings } from '../../components/Placings'
import { ScoreMark } from '../../components/ScoreMark'
import { STATE_LABEL, nextPoll } from '../../components/eventState'
import { holeClass } from '../../components/holeMarks'
import { usePlayerPaths } from '../../components/playerPaths'
import { withToPar } from '../../components/toPar'
import { usePolling } from '../../hooks/usePolling'

/**
 * One week, however far through it is.
 *
 * # Why this replaces two pages
 *
 * `/events/{id}` was the RECORD and `/events/{id}/scoreboard` was the live
 * derivation, and mid-round they described the same match differently: the
 * record published the match verdict from the STORED row, which is unwritten
 * until the week is scored, so its outcome column printed the literal word
 * `in_progress` where the scoreboard said "Mike 2 up". Two URLs, one event, two
 * answers. Commissioner, 2026-11-03: *"I wrote this. So others will def be
 * confused."*
 *
 * So the server now publishes the engine's verdict on the event route and this
 * page renders it, in every state, with no branch. `status`, `margin` and
 * `winner_player_id` are still in the payload as the record and are
 * deliberately not read here.
 *
 * # It switches on STATE for two things only
 *
 * How long until the next read (`nextPoll`), and what the caveat says.
 * Everything else reads off what the payload HAS — `placings` means scored,
 * `scorecards` means there is a card, `matches` means a paired format. Deriving
 * those from `state` instead gets the awkward real events wrong: a `scheduled`
 * week already has fixtures, and an imported night is `complete` with no card
 * at all.
 *
 * # A polled page cannot hold disclosure state in the DOM
 *
 * `<details open={...}>` would be reset by React on every poll, snapping each
 * card back closed ten seconds after a reader opened it. So each card's open
 * state is React state rather than the DOM's.
 */
export function Week() {
  const { eventId } = useParams()
  const id = Number(eventId)
  const live = usePolling(
    (s) => api.event(id, s),
    [id],
    (d: EventDetail) => nextPoll(d.state),
  )

  if (live.loading) return <LiveLoading what="the week" />
  if (!live.data) {
    return <Failed what="This week" error={live.error ?? new Error('nothing loaded')} />
  }
  return <WeekView detail={live.data} live={live} />
}

function WeekView({
  detail,
  live,
}: {
  detail: EventDetail
  live: ReturnType<typeof usePolling<EventDetail>>
}) {
  const { pathFor } = usePlayerPaths()

  // What the payload HAS, not what its state implies.
  const scored = detail.placings.length > 0
  const paired = detail.matches.length > 0

  // Names from the payload itself. The matches carry theirs now, and every
  // other collection already did, so this page needs no roster request.
  const names = new Map<string, string>()
  for (const c of detail.scorecards) names.set(c.player_id, c.display_name)
  for (const p of detail.placings) names.set(p.player_id, p.display_name)
  for (const m of detail.matches) {
    names.set(m.player_a_id, m.player_a_name)
    names.set(m.player_b_id, m.player_b_name)
  }
  const name = (pid?: string | null) => (pid ? (names.get(pid) ?? pid) : '—')

  // A hole's map is offered only where it has shots: every week played before
  // shots were captured has none, and a link to an empty map is a dead end.
  const withShots = new Set(detail.holes_with_shots ?? [])
  const matchHoleHref = (matchId: number) => (hole: number) =>
    withShots.has(hole) ? `/events/${detail.id}/holes/${hole}?match=${matchId}` : undefined

  return (
    <section className="week-page" data-testid="week">
      <h2>
        Week {detail.week_number} · {detail.course}
      </h2>

      <p className="note">
        <time dateTime={detail.scheduled_date}>{detail.scheduled_date}</time> · {detail.tee} tee ·{' '}
        {detail.holes_expected} holes · par {detail.par || '—'} · {detail.event_type.display_name}{' '}
        <span className={`badge badge-${detail.state}`}>{STATE_LABEL[detail.state]}</span>
        {' · '}
        <Link to={`/events/${detail.id}/stroke-sheet`}>Stroke sheet</Link>
        {' · '}
        <Link to={`/events/${detail.id}/tracker`}>Round tracker</Link>
        {' · '}
        <Link to={`/events/${detail.id}/projection`}>Projection</Link>
      </p>

      {/* Only while it is actually asking. The dateline says "updates on its
          own", which is false over a record that cannot change. */}
      {live.polling && (
        <LiveStatus
          loadedAt={live.loadedAt}
          sourceUpdatedAt={detail.updated_at}
          sourceStale={detail.stale}
          failures={live.failures}
          error={live.error}
        />
      )}

      <Caveat detail={detail} />

      <h3>Matches</h3>
      {!paired ? (
        <Empty>This week has no pairings — it is played as a field.</Empty>
      ) : (
        <div className="week-matches">
          {detail.matches.map((m) => (
            <article key={m.id} className="week-match" data-testid={`match-${m.id}`}>
              <div className="week-match-head">
                <h4>
                  {m.player_a_name} v {m.player_b_name}
                </h4>
                {/* The engine's phrasing. Never `m.status`, which is the stored
                    row and reads `in_progress` for most of a night. */}
                <p className={`week-match-status ${m.settled ? 'is-settled' : ''}`}>
                  <strong data-testid={`status-${m.id}`}>{m.status_text}</strong>
                  {m.outcome_note && <em className="mark mark-note"> — recorded</em>}
                  {/* **Why the later holes read "over."** A match settles on
                      holes remaining, so a 4&2 leaves two holes that scored
                      nothing — without saying so, the grid below looks like
                      missing data rather than a finished match (FR-035h). */}
                  {m.settled ? (
                    <span className="week-match-note" data-testid={`settled-${m.id}`}>
                      {m.margin ? `${m.margin} — ` : ''}the match is over; the holes below no longer
                      score
                    </span>
                  ) : (
                    m.holes_remaining > 0 && (
                      <span className="week-match-note">
                        {' '}
                        · {m.holes_remaining} {m.holes_remaining === 1 ? 'hole' : 'holes'} to play
                      </span>
                    )
                  )}
                </p>
              </div>
              <MatchHoles match={m} name={name} holeHref={matchHoleHref(m.id)} />
            </article>
          ))}
        </div>
      )}
      {detail.bye_player_id && (
        <p className="note">
          {name(detail.bye_player_id)} had the bye — and still played, and still counts toward the
          low net.
        </p>
      )}

      {/* ONE field table, chosen by what the week has. They never render
          together: two answers to "how did the field do" on one page is the
          thing this page exists to stop. */}
      <h3>The field</h3>
      {scored ? (
        <Placings rows={detail.placings} testId="event-placings" />
      ) : detail.provisional_low_net.length > 0 ? (
        <NetSoFar rows={detail.provisional_low_net} eventId={detail.id} />
      ) : (
        <Empty>Nothing is recorded until the round has been played.</Empty>
      )}

      {withShots.size > 0 && (
        <nav className="hole-strip" aria-label="Holes with shots" data-testid="hole-strip">
          <span className="note">Shots by hole:</span>
          {[...withShots]
            .sort((x, y) => x - y)
            .map((h) => (
              <Link key={h} to={`/events/${detail.id}/holes/${h}`} data-testid={`hole-strip-${h}`}>
                {h}
              </Link>
            ))}
        </nav>
      )}

      <h3>Scorecards</h3>
      {detail.scorecards.length === 0 ? (
        <Empty>
          {detail.state === 'complete'
            ? "This week's outcomes are recorded, but its hole-by-hole scores are not — the result was imported from the league's records rather than played through the application."
            : 'No scores yet.'}
        </Empty>
      ) : (
        <div className="week-cards">
          {detail.scorecards.map((card) => (
            <Card
              key={card.player_id}
              card={card}
              expected={detail.holes_expected}
              pathFor={pathFor}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/** What the state means for what is below it, in one sentence. */
function Caveat({ detail }: { detail: EventDetail }) {
  const awaiting = detail.awaiting ?? []
  switch (detail.state) {
    case 'scheduled':
      return (
        <p className="note note-provisional">
          This week has not been played. The fixtures are below;{' '}
          <Link to={`/events/${detail.id}/stroke-sheet`}>the stroke sheet</Link> shows where the
          strokes fall.
        </p>
      )
    case 'in_progress':
      return (
        <p className="note note-provisional">
          Being played now. Everything below is computed from the card as it stands, and nothing is
          awarded until the round is scored.
        </p>
      )
    case 'paused':
      return (
        <p className="note note-provisional">
          Paused. Nothing is completed during a pause, so the card below will not move until play
          resumes.
        </p>
      )
    case 'provisional':
      return (
        <p className="note note-provisional" data-testid="week-awaiting">
          Scored, and not final. The points below are in the standings, and they may still move:
          {awaiting.length === 0 ? (
            <> every card is in, so this is waiting only to be closed.</>
          ) : (
            <>
              {' '}
              still waiting on{' '}
              {awaiting.map((a, i) => (
                <span key={a.player_id}>
                  {i > 0 && ', '}
                  <strong>{a.display_name}</strong> ({a.holes} of {a.holes_expected})
                </span>
              ))}
              .
            </>
          )}
        </p>
      )
    default:
      return null
  }
}

/**
 * One player's card, collapsible and **closed by default at every width**.
 *
 * Five cards of hole grid is about a thousand pixels of detail between a reader
 * and the bottom of the page, and the summary line carries what most readers
 * came for — holes in, gross and net against par — so the grid is what you open
 * when you want to check a hole. Commissioner, 2026-11-03: *"start the score
 * cards collapsed."*
 *
 * **Not varied by viewport and not varied by event state.** An earlier version
 * opened them on a desktop, which made the page two different shapes and meant
 * a rotate changed it under you; varying by state would collapse the cards the
 * moment a week was scored.
 *
 * `<details>` rather than a button because every public view is asserted to
 * carry no button, no input and no form (FR-047c).
 */
function Card({
  card,
  expected,
  pathFor,
}: {
  card: PlayerScorecard
  expected: number
  pathFor: (id: string) => string
}) {
  const [open, setOpen] = useState(false)

  return (
    <details
      className="week-card"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      data-testid={`card-${card.player_id}`}
    >
      <summary>
        <span className="week-card-name">{card.display_name}</span>
        <span className="week-card-totals">
          {card.holes_assembled} of {expected} holes · gross{' '}
          {withToPar(card.gross_total, card.gross_to_par)} · net{' '}
          {withToPar(card.net_total, card.net_to_par)}
        </span>
      </summary>

      <p className="note">
        <Link to={pathFor(card.player_id)}>{card.display_name}</Link>
        {!card.matches_expected && (
          <em className="mark mark-warn">
            {' '}
            · {card.holes_assembled} of {expected} holes
          </em>
        )}
      </p>

      <div className="scroll-x">
        <table className="table scorecard-table">
          <thead>
            <tr>
              <th scope="col">Hole</th>
              {card.holes.map((h) => (
                <th key={h.hole} scope="col">
                  {h.hole}
                </th>
              ))}
              <th scope="col">Tot</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Par</th>
              {card.holes.map((h) => (
                <td key={h.hole}>{h.par}</td>
              ))}
              <td className="strong">{card.par_total}</td>
            </tr>
            <tr>
              <th scope="row">Strokes</th>
              {card.holes.map((h) => (
                <td key={h.hole} className="allocated">
                  {h.strokes_allocated === '0' ? '·' : h.strokes_allocated}
                </td>
              ))}
              <td className="strong">{card.strokes_total}</td>
            </tr>
            <tr>
              <th scope="row">Gross</th>
              {card.holes.map((h) => (
                <td key={h.hole} className={holeClass(h)} data-testid={`hole-${h.hole}`}>
                  <ScoreMark score={h.gross} par={h.par} />
                  <ProvenanceMark hole={h} />
                </td>
              ))}
              <td className="strong">{withToPar(card.gross_total, card.gross_to_par)}</td>
            </tr>
            <tr>
              <th scope="row">Net</th>
              {card.holes.map((h) => (
                <td key={h.hole}>
                  <ScoreMark score={h.net} />
                </td>
              ))}
              <td className="strong">{withToPar(card.net_total, card.net_to_par)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <Replays holes={card.holes} />
    </details>
  )
}
